"use client";

import { ChevronDown, ChevronRight, FileText } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { cn } from "@/lib/utils";

export interface DocsSidebarItem {
  title: string;
  href?: string;
  items?: DocsSidebarItem[];
  defaultOpen?: boolean;
  disabled?: boolean;
}

interface DocsSidebarProps {
  items: DocsSidebarItem[];
  className?: string;
}

interface SidebarSectionProps {
  item: DocsSidebarItem;
  pathname: string;
  level?: number;
}

function SidebarSection({ item, pathname, level = 0 }: SidebarSectionProps) {
  const [isOpen, setIsOpen] = useState(item.defaultOpen ?? false);
  const hasChildren = item.items && item.items.length > 0;
  // Normalize paths by removing trailing slashes for comparison
  const normalizedPathname = pathname.replace(/\/$/, '') || '/';
  const normalizedHref = item.href?.replace(/\/$/, '') || '/';
  const isActive = item.href && normalizedPathname === normalizedHref;

  if (!hasChildren && item.href) {
    if (item.disabled) {
      const disabledContent = (
        <span
          className={cn(
            "flex items-center gap-2 py-2 px-3 text-sm rounded-lg cursor-not-allowed",
            level > 0 && "ml-4",
            "text-text-muted/50"
          )}
        >
          {level > 0 && <FileText className="w-4 h-4 shrink-0" />}
          {item.title}
        </span>
      );

      return disabledContent;
    }

    return (
      <Link
        href={item.href}
        className={cn(
          "flex items-center gap-2 py-2 px-3 text-sm rounded-lg transition-colors",
          level > 0 && "ml-4",
          isActive
            ? "text-primary bg-primary/10 font-medium"
            : "text-text-secondary hover:text-text hover:bg-bg-alt"
        )}
      >
        {level > 0 && (
          <FileText className={cn("w-4 h-4 shrink-0", isActive && "text-primary")} />
        )}
        {item.title}
      </Link>
    );
  }

  return (
    <div className={cn(level > 0 && "ml-4")}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center justify-between w-full py-2 px-3 text-sm font-medium rounded-lg transition-colors",
          isActive
            ? "text-primary bg-primary/5"
            : "text-text hover:bg-bg-alt"
        )}
      >
        <span>{item.title}</span>
        {hasChildren && (
          <span className="text-text-muted">
            {isOpen ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </span>
        )}
      </button>
      {hasChildren && isOpen && (
        <div className="mt-1 space-y-0.5">
          {item.items!.map((child, index) => (
            <SidebarSection
              key={index}
              item={child}
              pathname={pathname}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function DocsSidebar({ items, className }: DocsSidebarProps) {
  const pathname = usePathname();

  return (
    <nav className={cn("py-4", className)} aria-label="Documentation navigation">
      <div className="space-y-1">
        {items.map((item, index) => (
          <SidebarSection key={index} item={item} pathname={pathname} />
        ))}
      </div>
    </nav>
  );
}
